import type { Bundle } from '../types';

/**
 * Pseudo-accent map: replaces ASCII vowels with accented variants.
 * Used by en-XA to make hardcoded (non-translated) strings visually obvious —
 * they appear as plain Latin in a sea of accented characters.
 */
const ACCENT_MAP: Record<string, string> = {
  a: 'á',
  e: 'é',
  i: 'í',
  o: 'ó',
  u: 'ú',
  A: 'Á',
  E: 'É',
  I: 'Í',
  O: 'Ó',
  U: 'Ú',
};

/**
 * Transforms a single string value into its pseudo-accented form.
 *
 * Strategy: replace ASCII vowels with accented equivalents, then wrap the
 * entire value in square brackets. ICU format patterns ({count, plural, ...})
 * are left structurally intact — only the literal text portions are accented,
 * which is correct because format patterns are not translatable prose.
 *
 * The bracket wrapper is applied at the string level (not per-segment) so the
 * i18n runtime can format ICU patterns before the wrapper is visible to users.
 * This mirrors Google's Android pseudo-locale approach.
 */
function pseudoAccent(str: string): string {
  const accented = str.replace(/[aeiouAEIOU]/g, (m) => ACCENT_MAP[m] ?? m);
  return `[${accented}]`;
}

/**
 * Returns a new bundle where every value is pseudo-accented.
 *
 * Use this bundle with `?locale=en-XA` in dev mode to detect hardcoded
 * English strings that bypass the i18n system — they will appear as plain
 * Latin while all translated strings appear accented and bracketed.
 */
export function getXABundle(base: Bundle): Bundle {
  return Object.fromEntries(
    Object.entries(base).map(([k, v]) => [k, pseudoAccent(v)]),
  );
}
